import { PurposeRestriction, RestrictionType, TCModel, TCString } from '@iabtcf/core'
import { createLog } from './logger'

const log = createLog()

function createTcModelForNewUser (gvl, publisherRestrictions) {
  log('creating TCModel for new user')
  const tcModel = new TCModel()
  tcModel.cmpId = 27
  tcModel.cmpVersion = 3
  // siteSpecific Storage (ie not global)
  tcModel.isServiceSpecific = true
  tcModel.publisherCountryCode = 'GB'

  const vendorIds = publisherRestrictions
  if (publisherRestrictions && publisherRestrictions.length) {
    [3, 4].forEach(purposeId => {
      vendorIds.forEach(vendorId => {
        try {
          const purposeRestriction = new PurposeRestriction()
          purposeRestriction.purposeId = purposeId
          purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
          // vendorID and restriction
          log(`restricting purposeId: ${purposeId} for vendorId: ${vendorId}`)
          tcModel.publisherRestrictions.add(vendorId, purposeRestriction)
        } catch (e) {
          log('unable to restrict', purposeId, vendorId)
        }
      })
    })
  }
  tcModel.gvl = gvl
  tcModel.publisherConsents.set([1, 2, 3, 4, 5, 6, 7, 8, 9, 10])
  tcModel.publisherLegitimateInterests.set([5, 6, 8, 10])

  return tcModel
}

function createTcModelForExistingUser (gvl, tcString) {
  log('creating TCModel for existing user')
  const tcModel = TCString.decode(tcString)

  // controller was originally released with consentScreen=0, this hack will update all those users
  if (tcModel.consentScreen === 0) {
    tcModel.consentScreen = 1
  }
  tcModel.gvl = gvl
  return tcModel
}

export default class {
  constructor ({ tcString, gvl, publisherRestrictions }) {
    this.gvl = gvl
    this.publisherRestrictions = publisherRestrictions
    this.model = tcString ? createTcModelForExistingUser(gvl, tcString) : createTcModelForNewUser(gvl, publisherRestrictions)

    this.byPurposeVendorMap = this.byPurposeVendorMap.bind(this)
    this.gvlIsReady = this.gvlIsReady.bind(this)
    this.updated = this.updated.bind(this)
    this.tcString = this.tcString.bind(this)
    this.updateUserPreferences = this.updateUserPreferences.bind(this)
    this.consentAll = this.consentAll.bind(this)
    this.dissentAll = this.dissentAll.bind(this)
    this.hasUserDissentedToAll = this.hasUserDissentedToAll.bind(this)
    this.hasUserConsentedToAll = this.hasUserConsentedToAll.bind(this)
    this.hasUserConsentedToVendors = this.hasUserConsentedToVendors.bind(this)
  }

  reload (tcString) {
    const newModel = createTcModelForExistingUser(this.gvl, tcString)
    log('reloading tcModel', tcString, newModel)

    this.model = newModel
  }

  byPurposeVendorMap () {
    return this.model.gvl.byPurposeVendorMap
  }

  gvlIsReady () {
    return this.model.gvl.readyPromise
  }

  updated () {
    this.model.updated()
  }

  tcString () {
    log('encoding', this.model)
    return TCString.encode(this.model)
  }

  updateUserPreferences ({ vendorConsents, vendorLegitimateInterests, purposeConsents, purposeLegitimateInterests, specialFeatureOptins, consentScreen }) {
    this.model.vendorConsents.empty()
    this.model.vendorConsents.set(vendorConsents)

    this.model.vendorLegitimateInterests.empty()
    this.model.vendorLegitimateInterests.set(vendorLegitimateInterests)

    this.model.purposeConsents.empty()
    this.model.purposeConsents.set(purposeConsents)
    this.model.purposeLegitimateInterests.empty()
    this.model.purposeLegitimateInterests.set(purposeLegitimateInterests)

    this.model.specialFeatureOptins.empty()
    this.model.specialFeatureOptins.set(specialFeatureOptins)

    this.model.consentScreen = consentScreen
  }

  consentAll ({ consentScreen }) {
    this.model.setAll()
    this.model.consentScreen = consentScreen
    this.model.updated()
  }

  dissentAll ({ consentScreen }) {
    this.model.unsetAll()
    this.model.consentScreen = consentScreen
    this.model.updated()
  }

  hasUserDissentedToAll () {
    return !this.model.specialFeatureOptins.size &&
      !this.model.purposeConsents.size &&
      !this.model.purposeLegitimateInterests.size &&
      !this.model.vendorConsents.size &&
      !this.model.vendorLegitimateInterests.size
  }

  hasUserConsentedToAll () {
    const vendorsWithPurposes = Object.keys(this.model.gvl.vendors).filter(id => this.model.gvl.vendors[id].purposes.length > 0).map(id => parseInt(id))
    const vendorsWithLegitimateInterest = Object.keys(this.model.gvl.vendors).filter(id => this.model.gvl.vendors[id].legIntPurposes.length > 0).map(id => parseInt(id))
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    // https://github.com/InteractiveAdvertisingBureau/iabtcf-es/blob/fd137351359c346ce2eb45ecc4408909bebf8c41/modules/core/src/encoder/SemanticPreEncoder.ts#L25
    const purposesIdsWithLegitimateInterest = purposesIds.filter(id => id !== 1)
    const specialFeatureOptinIds = Object.keys(this.model.gvl.specialFeatures).map(id => parseInt(id))
    const hasConsentedToAllSpecialFeatureOptins = specialFeatureOptinIds.every(id => this.model.specialFeatureOptins.has(id))
    const hasConsentedToAllPurposes = purposesIds.every(id => this.model.purposeConsents.has(id))
    const hasConsentedToAllPurposesLegInt = purposesIdsWithLegitimateInterest.every(id => this.model.purposeLegitimateInterests.has(id))
    const hasConsentedToAllVendorPurposes = vendorsWithPurposes.every(id => this.model.vendorConsents.has(id))
    const hasConsentedToAllVendorLegInt = vendorsWithLegitimateInterest.every(id => this.model.vendorLegitimateInterests.has(id))
    return hasConsentedToAllVendorLegInt && hasConsentedToAllVendorPurposes && hasConsentedToAllPurposes && hasConsentedToAllPurposesLegInt && hasConsentedToAllSpecialFeatureOptins
  }

  hasUserConsentedToVendors (ids) {
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const purposeRestrictions = [3, 4].map(purposeId => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      return purposeRestriction
    })
    const vendorIdsDissentedByPurpose = new Set(purposesIds.filter(id => !this.model.purposeConsents.has(id)).map(id => Array.from(this.model.gvl.byPurposeVendorMap[id] && this.model.gvl.byPurposeVendorMap[id].consent || [])).flat())
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some(restriction => {
        return this.model.publisherRestrictions.vendorHasRestriction(id, restriction) && !this.model.purposeConsents.has(restriction.purposeId)
      })
    }
    const preferenceForIds = ids.map(id => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const hasConsented = !!this.model.vendorConsents.has(id) && !vendorIdsDissentedByPurpose.has(id) && !constrainedByFlexPurpose
      return {
        id,
        hasConsented,
        ...(constrainedByFlexPurpose ? { constrainedByFlexPurpose } : {})
      }
    })
    return preferenceForIds
    // TODO:
    // specialFeatureOptins?? awaiting feedback
  }

  consentedVendors () {
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const purposeRestrictions = [3, 4].map(purposeId => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      return purposeRestriction
    })
    const model = TCString.decode(TCString.encode(this.model))
    const vendorIdsDissentedByPurpose = new Set(purposesIds.filter(id => !model.purposeConsents.has(id)).map(id => Array.from(this.model.gvl.byPurposeVendorMap[id] && this.model.gvl.byPurposeVendorMap[id].consent || [])).flat())
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some(restriction => {
        return model.publisherRestrictions.vendorHasRestriction(id, restriction) && !model.purposeConsents.has(restriction.purposeId)
      })
    }
    const preferenceForIds = Array.from(this.model.gvl.vendorIds).filter(id => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const vendorRestrictedToConsent = purposeRestrictions.some(restriction => model.publisherRestrictions.vendorHasRestriction(id, restriction))
      const hasConsented = (vendorRestrictedToConsent ? model.vendorConsents.has(id) : (model.vendorConsents.has(id) || model.vendorLegitimateInterests.has(id))) &&
        !vendorIdsDissentedByPurpose.has(id) &&
        !constrainedByFlexPurpose
      return hasConsented
    })
    return preferenceForIds
  }

  consentedVendorsDetails () {
    const purposesIds = Object.keys(this.model.gvl.purposes).map(id => parseInt(id))
    const purposeRestrictions = [3, 4].map(purposeId => {
      const purposeRestriction = new PurposeRestriction()
      purposeRestriction.purposeId = purposeId
      purposeRestriction.restrictionType = RestrictionType.REQUIRE_CONSENT
      return purposeRestriction
    })
    const vendorIdsDissentedByPurpose = new Set(purposesIds.filter(id => !this.model.purposeConsents.has(id)).map(id => Array.from(this.model.gvl.byPurposeVendorMap[id] && this.model.gvl.byPurposeVendorMap[id].consent || [])).flat())
    const vendorIdDissentedByFlexiblePurpose = (id) => {
      return purposeRestrictions.some(restriction => {
        return this.model.publisherRestrictions.vendorHasRestriction(id, restriction) && !this.model.purposeConsents.has(restriction.purposeId)
      })
    }
    const preferenceForIds = Array.from(this.model.gvl.vendorIds).map(id => {
      const constrainedByFlexPurpose = vendorIdDissentedByFlexiblePurpose(id)
      const vendorRestrictedToConsent = purposeRestrictions.some(restriction => this.model.publisherRestrictions.vendorHasRestriction(id, restriction))
      const hasConsented = (vendorRestrictedToConsent ? this.model.vendorConsents.has(id) : (this.model.vendorConsents.has(id) || this.model.vendorLegitimateInterests.has(id))) &&
        !vendorIdsDissentedByPurpose.has(id) &&
        !constrainedByFlexPurpose
      return {
        id,
        hasConsented,
        consentedTests: {
          consented: this.model.vendorConsents.has(id),
          legitimateInterest: this.model.vendorLegitimateInterests.has(id),
          vendorRestrictedToConsent,
          dissentedByPurpose: vendorIdsDissentedByPurpose.has(id),
          contrainedByFlexPurpose: constrainedByFlexPurpose
        }
      }
    })
    return preferenceForIds
  }

  setGvl (gvl) {
    this.model.gvl = gvl
  }
}
